import { setupModeColors } from "../SetupMode/colors";
import type { ChangeEvent, ReactNode } from "react";
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { styled } from "@linaria/react";
import * as io from "io-ts";
import cn from "classnames";
import { color, zIndex } from "@ui";
import createGlobalState from "@ui/lib/createGlobalState";
import t from "../../i18n";
import CommentList, { MAX_COMMENT_LENGTH } from "./List";
import { HIDE_ON_EXPORT } from "@ui/lib/downloadPage";
import classNames from "classnames";
import { ResetElement } from "@ui/Filter/Reset";
import FilterSelect from "@ui/Filter/FilterSelect";
import { indexBy } from "diagnostics-lib/iterable";
import { hashCode } from "diagnostics-lib/object";
import { notUndefined } from "diagnostics-lib/typescript";
import useSelectedAnalyses from "../Project/Analysis/useSelectedAnalyses";
import useActiveAnalysesStream from "../../api/useActiveAnalysesStream";
import { useComments } from "../../api/useComments";
import type { Project } from "../../api/useProjects";
import { useUser } from "../../api/useUser";
import { usePost, usePut } from "../../api/useCall";
import { useCanUserDoOnProject } from "../canUserDo";
import ButtonIconV2, { ButtonIconMarker } from "@ui/ButtonIconV2";
import SearchInputV2 from "@ui/Input/SearchInputV2";
import { AedLine, Attachment2, Filter3Fill, FlagLine } from "@ui/IconV2/react";
import type { Source } from "../../api/useSources";
import { useProjectPeriod } from "../Project/useProjectPeriod";
import { css } from "@linaria/core";
import { colors } from "@ui/Theme";
import { analysisTitleShort } from "../Project/Analysis/analysisTitle";
import useToasts from "@ui/Toast/useToasts";
import Cta from "./components/Cta";
import { useFaultEventAccess } from "../Project/Events/hooks/useFaultEventAccess";
import { MessageEditorV2 } from "@ui/MessageEditorV2";
import { ToolbarSeparator } from "@ui/Markdown/Toolbar/MarkdownToolbar";
import { ToolbarButton } from "@ui/Markdown/Toolbar/ToolbarButton";
import { MarkdownSection } from "@ui/Markdown/MarkdownSection";
import { Tag, tagList } from "@ui/Tag";
import Ellipsis from "@ui/Ellipsis";
import { useCommentEventAssociation } from "./hooks/useCommentEventAssociation";
import EventMenu from "./components/EventMenu";
import { useEvents } from "../../api/useEvents";
import { useSettings } from "../../api/useSettings";

import SetupCommentToggle from "./components/SetupCommentToggle";
import { setupCommentEditor } from "./styles/messageEditorStyles";

import useProjectSetupMode from "../SetupMode/useProjectSetupMode";
import useSetupCommentType from "./hooks/useSetupCommentType";

const OLD_FF_HACK_CN = "old-firefox-hack";

interface P {
  project: Project;
  source: Source;
  eventId: string | null;
  associatedEventIds?: readonly string[];
}
export interface CommentWithAnswers {
  id: number;
  analysisId?: number;
  title: ReactNode;
  date: number;
  content: string;
  allowEdit: boolean;
  allowDelete: boolean;
  answers: CommentWithAnswers[];
  subHeader?: ReactNode;
  allowAnswer?: boolean;
}

function ProjectComments({ project, source, eventId, associatedEventIds = [] }: P) {
  const { addToast } = useToasts();
  const user = useUser();
  const hasFaultEventAcces = useFaultEventAccess();
  const createComment = usePost("/comments");
  const canUseComment = useCanUserDoOnProject("PROJECT_WRITE", project.id);
  const { isSetupModeEnabledOnProject } = useProjectSetupMode(project.id);
  const canCreateSetupComments = isSetupModeEnabledOnProject && canUseComment;
  const {
    type: commentType,
    toggle: toggleCommentType,
    reset: resetCommentType,
  } = useSetupCommentType(project.id, canCreateSetupComments);
  const comments = useComments(project.id).filter(c => c.dashboardId === null);

  const { activeAnalyses } = useActiveAnalysesStream(project.id);
  const { selectedAnalyses, setBoundary } = useSelectedAnalyses(project.id, activeAnalyses);
  const analysis = selectedAnalyses[0];
  const [, , , setPeriodCenteredOnDates] = useProjectPeriod({
    source,
    project,
    analyses: activeAnalyses,
    boundaries: "active-analyses",
  });

  /** Part Filter */
  const [analysisFiltered, setAnalysisFiltered] = useCommentsFilteredOnAnalysis({
    scope: project.id,
  });
  const [showCommentFilters, setShowCommentFilters] = useShowCommentFilters({
    scope: eventId ?? project.id,
  });
  const [search, setSearch] = useCommentSearch({ scope: eventId ?? project.id });
  const handleSearchChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setSearch(e.currentTarget.value);
    },
    [setSearch],
  );

  /** Part Users */
  const [filteredUserHashs, setFilteredUserHash] = useFilteredCommentUsers({
    scope: eventId ?? project.id,
  });
  const userIndex = useMemo(
    () =>
      indexBy(
        comments.map(c => c.author.name ?? c.author.id),
        c => hashCode(c),
      ),
    [comments],
  );
  const userOptions = useMemo(
    () =>
      [...userIndex.entries()].map(([hash, userName]) => ({
        value: hash,
        label: userName,
      })),
    [userIndex],
  );
  const filteredUsers = useMemo(
    () => filteredUserHashs.map(hash => userIndex.get(hash)).filter(notUndefined),
    [filteredUserHashs, userIndex],
  );

  /** Part Events */
  const settings = useSettings();
  const createFaultEvent = usePut("/projects/:projectId(uuid)/faultEvents");
  const events = useEvents(project.id, settings.MANUAL_EVENTS_DEV_MODE_ENABLED);
  const eventsById = useMemo(() => new Map(events.map(event => [event.id, event])), [events]);
  const { selection, filters, buildEventPublishPayload } = useCommentEventAssociation({
    scope: eventId ?? project.id,
    events,
    initialEventId: eventId,
    hasEventAccess: hasFaultEventAcces === true,
  });
  const filterEventOptions = useMemo(
    () =>
      filters.options
        .map(option => {
          const event = eventsById.get(option.value);
          if (event?.type === "MANUAL") {
            return { ...option, badge: t("project.manual_fault_event.tag.manual") };
          }
          return option;
        })
        .sort((a, b) => a.label.localeCompare(b.label)),
    [eventsById, filters.options],
  );
  const forcedEventIds = useMemo(
    () => (eventId === null ? filters.values : [eventId, ...associatedEventIds]),
    [eventId, filters.values, associatedEventIds],
  );

  // Scroll
  const commentListDivRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (commentListDivRef.current && oldFirefoxHackNeeded) {
      commentListDivRef.current.scrollTop = commentListDivRef.current.scrollHeight;
    }
  }, []);
  useEffect(() => {
    if (commentListDivRef.current && oldFirefoxHackNeeded) {
      commentListDivRef.current.scrollTop = commentListDivRef.current.scrollHeight;
    }
  }, []);

  // Handler Analysis
  const checkIfInRange = useCallback(
    (date: number): boolean => {
      const startBoundary = selectedAnalyses[0];
      if (!startBoundary) return false;
      const endBoundary = selectedAnalyses[selectedAnalyses.length - 1];
      if (!endBoundary) throw new Error("it should not happen");

      return Number(startBoundary.date) <= date && date <= Number(endBoundary.date);
    },
    [selectedAnalyses],
  );
  const isAnalysisInRange = useCallback(
    (analysisId: string | null) => selectedAnalyses.some(analysis => analysis.id === analysisId),
    [selectedAnalyses],
  );
  const filteredComments = useMemo(() => {
    if (analysisFiltered) {
      return comments.filter(comment => isAnalysisInRange(comment.analysisId));
    }
    return comments;
  }, [comments, analysisFiltered, isAnalysisInRange]);

  const setAnalysis = useCallback(
    (pivot: number | null) => {
      setBoundary({ pivot, to: null });
      if (pivot) setPeriodCenteredOnDates({ pivot, to: pivot });
    },
    [setBoundary, setPeriodCenteredOnDates],
  );
  const toggleCurrentAnalysisOnly = useCallback(() => {
    setAnalysisFiltered(value => !value);
  }, [setAnalysisFiltered]);
  const toggleShowCommentFilters = useCallback(() => {
    setShowCommentFilters(value => !value);
  }, [setShowCommentFilters]);

  // CTA
  const hasMultipleAnalyses = selectedAnalyses.length > 1;
  const hasAnalysisOutsideOfEventRange = useMemo(() => {
    if (!analysis || selection.event?.type !== "DIAGNOSED") return false;
    return (
      analysis.date < new Date(selection.event.startsAt).getTime() ||
      analysis.date > new Date(selection.event.endsAt).getTime()
    );
  }, [analysis, selection.event]);
  const isAnalysisCTADisabled = useMemo(
    () => hasMultipleAnalyses || hasAnalysisOutsideOfEventRange || analysis === undefined,
    [analysis, hasMultipleAnalyses, hasAnalysisOutsideOfEventRange],
  );
  const [isAssociatedWithAnalysis, setIsAssociatedWithAnalysis] =
    useState<boolean>(!isAnalysisCTADisabled);
  useEffect(() => {
    setIsAssociatedWithAnalysis(!isAnalysisCTADisabled);
  }, [isAnalysisCTADisabled]);

  const toolBarOptions = useMemo(
    () => (
      <>
        <div className={divider} />
        <Cta ctaType="edit-attach_files" check={false} disabled onClick={() => null} />
        <div className={divider} />
        <Cta
          ctaType={
            hasAnalysisOutsideOfEventRange
              ? "edit-analysis-outside-of-event-range"
              : "edit-analysis"
          }
          check={isAssociatedWithAnalysis}
          disabled={isAnalysisCTADisabled}
          onClick={() => setIsAssociatedWithAnalysis(s => !s)}
        />
        {hasFaultEventAcces === true ? (
          eventId === null ? (
            <EventMenu
              project={project}
              events={events}
              selectedEventId={selection.id}
              // eslint-disable-next-line react/jsx-handler-names
              onSelect={selection.setId}
            />
          ) : null
        ) : (
          <Cta ctaType="edit-event-under-development" check={false} disabled onClick={() => null} />
        )}
      </>
    ),
    [
      project,
      isAssociatedWithAnalysis,
      eventId,
      isAnalysisCTADisabled,
      hasAnalysisOutsideOfEventRange,
      hasFaultEventAcces,
      selection,
      events,
    ],
  );

  const resetEditorOptions = useCallback(() => {
    selection.reset();
    setIsAssociatedWithAnalysis(true);
    resetCommentType();
  }, [selection, setIsAssociatedWithAnalysis, resetCommentType]);

  const handlePublish = useCallback(
    async (parentId: string | null, comment: string) => {
      const parentComment = comments.find(c => c.id === parentId);
      const analysisId = parentComment
        ? parentComment.analysisId
        : isAssociatedWithAnalysis
          ? (analysis?.id ?? null)
          : null;

      const parentEventId =
        (parentComment?.faultEvent && parentComment.faultEvent.id) ??
        parentComment?.manualFaultEventId ??
        null;
      const parentEvent = parentEventId === null ? null : (eventsById.get(parentEventId) ?? null);
      const eventPublishPayload = buildEventPublishPayload(parentEvent);

      const baseBody = {
        projectId: project.id,
        analysisId,
        parentId,
        content: comment,
        type: parentComment?.type ?? commentType,
        dashboardId: null,
        blockCommentId: null,
      } as const;

      try {
        if (eventPublishPayload?.type === "DIAGNOSED") {
          if (analysisId !== null && hasAnalysisOutsideOfEventRange) {
            throw new Error("Analysis outside of event range, association not possible");
          }
          if (eventPublishPayload.shouldPersist) {
            await createFaultEvent({
              body: eventPublishPayload.faultEventPayload,
              routeParams: { projectId: project.id },
            });
          }
          await createComment({
            body: {
              ...baseBody,
              manualFaultEventId: null,
              faultEventId: eventPublishPayload.faultEventId,
            },
          });
        } else if (eventPublishPayload?.type === "MANUAL") {
          await createComment({
            body: {
              ...baseBody,
              manualFaultEventId: eventPublishPayload.manualFaultEventId,
              faultEventId: null,
            },
          });
        } else {
          await createComment({
            body: {
              ...baseBody,
              manualFaultEventId: null,
              faultEventId: null,
            },
          });
        }
        if (!parentComment) resetCommentType();
      } catch (error) {
        addToast("error", t("errors.backend_error"));
      }

      if (analysisId === null) {
        setAnalysisFiltered(false);
      }

      if (commentListDivRef.current && !parentComment)
        commentListDivRef.current.scrollTop = commentListDivRef.current.scrollHeight;
    },
    [
      hasAnalysisOutsideOfEventRange,
      commentType,
      resetCommentType,
      addToast,
      createFaultEvent,
      analysis,
      comments,
      eventsById,
      buildEventPublishPayload,
      createComment,
      isAssociatedWithAnalysis,
      project.id,
      setAnalysisFiltered,
    ],
  );
  const handlePublishNewComment = useCallback(
    (comment: string) => handlePublish(null, comment),
    [handlePublish],
  );

  return (
    <>
      <Filters>
        <FiltersFirstLine>
          <StyledSearchInput
            placeholder={t("commons.placeholder.search")}
            onChange={handleSearchChange}
            value={search}
          />
          <FilterButtonWrapper className={HIDE_ON_EXPORT}>
            <ButtonIconV2
              className={filterButton}
              title={t("comments.tooltip.toggle_current_analysis_comments")}
              checked={analysisFiltered}
              onClick={toggleCurrentAnalysisOnly}
            >
              <FlagLine size="l" />
            </ButtonIconV2>
            <ButtonIconV2
              className={filterButton}
              title={t("comments.tooltip.toggle_filters")}
              checked={showCommentFilters}
              onClick={toggleShowCommentFilters}
            >
              {(filteredUserHashs.length > 0 || (eventId === null && filters.hasActiveFilters)) && (
                <ButtonIconMarker />
              )}
              <Filter3Fill size="l" />
            </ButtonIconV2>
          </FilterButtonWrapper>
        </FiltersFirstLine>
        {showCommentFilters && (
          <FiltersSecondLine>
            <SyledFilterSelect
              title={t("comments.filters.user")}
              options={userOptions}
              values={filteredUserHashs}
              setValues={setFilteredUserHash}
            />
            {eventId === null && hasFaultEventAcces === true && (
              <SyledFilterSelect
                title={t("comments.filters.event")}
                options={filterEventOptions}
                values={filters.values}
                setValues={filters.setValues}
              />
            )}
            <ResetElement
              onClick={() => {
                setFilteredUserHash([]);
                if (eventId === null) filters.setValues([]);
              }}
              className={cn({
                active:
                  filteredUserHashs.length > 0 || (eventId === null && filters.hasActiveFilters),
              })}
            >
              {t("action.reset")}
            </ResetElement>
          </FiltersSecondLine>
        )}
      </Filters>
      <StyledCommentList
        // https://bugzilla.mozilla.org/show_bug.cgi?id=1042151
        className={classNames({ [OLD_FF_HACK_CN]: oldFirefoxHackNeeded })}
        ref={commentListDivRef}
        key={`${project.id}/${isSetupModeEnabledOnProject}`}
        project={project}
        checkIfInRange={checkIfInRange}
        comments={filteredComments}
        user={user}
        onPublish={handlePublish}
        setAnalysis={setAnalysis}
        search={search}
        users={filteredUsers}
        eventIds={forcedEventIds}
        eventById={eventsById}
        timezone={project.timezone}
        toolBarOptions={toolBarOptions}
      />
      {canUseComment ? (
        <div className={cn(addCommentInput, { [setupCommentInput]: commentType === "SETUP" })}>
          <MessageEditorV2
            placeholder={t("comments.create.placeholder")}
            onPublish={handlePublishNewComment}
            maxLength={MAX_COMMENT_LENGTH}
            onCancel={() => resetEditorOptions()}
            className={cn({ [setupCommentEditor]: commentType === "SETUP" })}
          >
            {(Toolbar, richText) => (
              <>
                <Toolbar>
                  <ToolbarSeparator />
                  <ToolbarButton
                    disabled
                    description={t("comments.tooltip.feature_under_development")}
                  >
                    <Attachment2 size="s" />
                  </ToolbarButton>
                  <ToolbarSeparator />
                  <ToolbarButton
                    checked={isAssociatedWithAnalysis}
                    disabled={isAnalysisCTADisabled}
                    onClick={() => setIsAssociatedWithAnalysis(s => !s)}
                    description={
                      hasAnalysisOutsideOfEventRange
                        ? t("comments.tooltip.analysis_outside_of_event_range")
                        : t("comments.tooltip.connect_current_analysis")
                    }
                  >
                    <FlagLine size="s" />
                  </ToolbarButton>
                  {!hasFaultEventAcces && (
                    <ToolbarButton description={t("comments.tooltip.feature_under_development")}>
                      <Attachment2 size="s" />
                    </ToolbarButton>
                  )}
                  {hasFaultEventAcces && (
                    <>
                      {eventId === null ? (
                        <EventMenu
                          project={project}
                          events={events}
                          selectedEventId={selection.id}
                          // eslint-disable-next-line react/jsx-handler-names
                          onSelect={selection.setId}
                        />
                      ) : null}
                    </>
                  )}
                </Toolbar>
                {richText}
                <MarkdownSection className={tagList}>
                  {isAssociatedWithAnalysis && analysis && (
                    <Tag size="s">
                      <FlagLine size="xs" />{" "}
                      <Ellipsis>
                        {analysisTitleShort(
                          { date: analysis.date, group: analysis.group, label: analysis.label },
                          project.timezone,
                        )}
                      </Ellipsis>
                    </Tag>
                  )}
                  {selection.event && (
                    <Tag size="s">
                      <AedLine size="xs" />
                      <Ellipsis>{selection.event.name}</Ellipsis>
                    </Tag>
                  )}
                </MarkdownSection>
              </>
            )}
          </MessageEditorV2>
          {canCreateSetupComments && (
            <SetupCommentToggle checked={commentType === "SETUP"} onChange={toggleCommentType} />
          )}
        </div>
      ) : (
        <Spacer />
      )}
    </>
  );
}

export default memo(ProjectComments);

//#region GLOBAL STATE
const useCommentsFilteredOnAnalysis = createGlobalState({
  storage: "session",
  key: "commentsFilteredOnAnalysis",
  type: io.boolean,
  defaultValue: false,
  scoped: true,
});

const useShowCommentFilters = createGlobalState({
  storage: "session",
  key: "showCommentFilters",
  type: io.boolean,
  defaultValue: false,
  scoped: true,
});

const useFilteredCommentUsers = createGlobalState({
  storage: "session",
  key: "filteredCommentUsers",
  type: io.array(io.string),
  defaultValue: [],
  scoped: true,
});

const useCommentSearch = createGlobalState({
  storage: "session",
  key: "commentSearch",
  type: io.string,
  defaultValue: "",
  scoped: true,
});
//#endregion GLOBAL STATE

//#region CSS
const divider = css`
  display: inline-flex;
  justify-self: center;
  background-color: ${colors.titanium200};
  width: 1px;
  height: auto;
`;

const Filters = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 16px;
  border-bottom: 1px solid;
  border-bottom-color: ${color.titanium200};
  padding: 24px;
  background-color: ${color.hydrogen};
  z-index: ${zIndex.fixedHeader};
`;

const FilterButtonWrapper = styled.div`
  display: flex;
  gap: 8px;
`;

const filterButton = css`
  align-self: center;
`;

const StyledSearchInput = styled(SearchInputV2)`
  flex-grow: 1;
`;

const FiltersFirstLine = styled.div`
  display: flex;
  gap: 12px;
`;
const FiltersSecondLine = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

const SyledFilterSelect = styled(FilterSelect)`
  width: fit-content;
`;

const addCommentInput = css`
  padding: 16px;
  background-color: ${color.titanium50};
`;

const setupCommentInput = css`
  && {
    background-color: ${setupModeColors.background};
  }
`;

const StyledCommentList = styled(CommentList)`
  height: 100%;
  overflow-y: auto;
  overscroll-behavior: contain;
  scroll-behavior: smooth;

  &.${OLD_FF_HACK_CN} {
    justify-content: flex-end;
  }
`;

const Spacer = styled.div`
  height: 40px;
`;
//#endregion CSS

//#region Functions
// https://bugzilla.mozilla.org/show_bug.cgi?id=1042151
const oldFirefoxHackNeeded = (() => {
  const match = window.navigator.userAgent.match(/Firefox\/([0-9]+)\./);
  const ver = match?.[1] ? parseInt(match[1]) : 0;
  return ver && ver < 81;
})();

//#endregion Functions
